<template>
  <IconButton
    v-if="isMobile"
    id="spotlight.button"
    name="spotlight.button"
    icon-name="material-symbols:search"
    variant="ghost"
    size="md"
    class="ms-auto"
    @click="open"
  />
  <BootstrapButton
    v-else
    id="spotlight.button"
    name="spotlight.button"
    variant="ghost"
    class="border shadow-sm bg-body"
    style="
      width: 75%;
      min-width: 18rem;
      margin-left: var(--spotlight-start, auto);
      margin-right: auto;
    "
    :aria-label="translate('Search or go to...')"
    @click="open"
  >
    <Icon icon="material-symbols:search" size="md" />
    <!-- Rotates through what the search can do. Hidden from assistive tech: the button's label is static -->
    <Transition name="hint" mode="out-in">
      <span
        :key="hint"
        class="flex-grow-1 small text-truncate text-start ms-1"
        aria-hidden="true"
        >{{ hint }}</span
      >
    </Transition>
    <kbd class="spotlight-key ms-3 px-1 text-body-secondary" aria-hidden="true">{{
      shortcutLabel
    }}</kbd>
  </BootstrapButton>
  <DialogDrawer
    id="spotlight.dialog"
    ref="dialog"
    :class="['spotlight-dialog', {'spotlight-dialog-loading': loading}]"
    placement="centre"
    modal
    @close="reset"
  >
    <BootstrapFormControl
      v-model="model"
      name="spotlight"
      :placeholder="translate('What are you looking for today?')"
      only-input
      autofocus
      class="p-2 border-bottom"
      @input="onInput"
      @enter="enter"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.left="sideways(-1, $event)"
      @keydown.right="sideways(1, $event)"
    >
      <Icon icon="material-symbols:search" size="md" />
    </BootstrapFormControl>
    <div
      v-if="!isMobile"
      class="spotlight-keys d-flex align-items-center small text-secondary px-3 py-2 border-bottom"
    >
      <kbd class="spotlight-key"><Icon icon="material-symbols:arrow-upward" size="sm" /></kbd>
      <kbd class="spotlight-key ms-1"
        ><Icon icon="material-symbols:arrow-downward" size="sm"
      /></kbd>
      <span class="ms-2">{{ translate('Navigate') }}</span>
      <template v-if="activeLabel !== undefined || (model.trim() && !loading)">
        <kbd class="spotlight-key ms-auto px-2">{{ translate('Enter') }}</kbd>
        <span class="ms-2">{{
          activeLabel === undefined ? translate('Search') : translate('Open')
        }}</span>
      </template>
    </div>
    <div
      v-if="list.size > 0"
      :class="[
        'spotlight-sections overflow-y-scroll h-100 mb-0 py-2 px-2',
        {'spotlight-flat': withoutHeadings},
      ]"
    >
      <!-- A search is under way: the headings below stay, their items are replaced by the results -->
      <ul
        v-if="loading"
        class="placeholder-glow placeholder-list spotlight-ghost"
        aria-hidden="true"
      >
        <li v-for="i in GHOST_ROWS" :key="i" class="w-100 mb-2">
          <i class="placeholder placeholder-lg ratio ratio-1x1 me-2"></i>
          <span :class="['placeholder placeholder-lg', `col-${placeholderNum(7, 12)}`]"></span>
        </li>
      </ul>
      <SettingsDialogListGroup
        v-if="sections.before.size > 0"
        :list="sections.before"
        :highlight="activeLabel"
        class="h-auto overflow-visible"
        @update:model-value="navigate"
      />
      <!-- The recent searches first, the items in two columns -->
      <div v-for="group in sections.leading" :key="[...group.keys()][0]" class="position-relative">
        <SettingsDialogListGroup
          :list="group"
          :highlight="activeLabel"
          class="spotlight-two-columns h-auto overflow-visible"
          @update:model-value="navigate"
        />
        <div v-if="[...group.values()][0].length > 0" class="spotlight-clear">
          <BootstrapButton
            name="spotlight.clear-recent"
            variant="link"
            size="sm"
            class="p-0"
            @click="clearRecent"
          >
            {{ translate('Clear') }}
          </BootstrapButton>
        </div>
      </div>
      <!-- Recent pages & suggestions side by side -->
      <div
        v-if="sections.row.length > 0"
        class="spotlight-row"
        :style="{'--columns': sections.row.length}"
      >
        <SettingsDialogListGroup
          v-for="group in sections.row"
          :key="[...group.keys()][0]"
          :list="group"
          :highlight="activeLabel"
          class="spotlight-column h-auto overflow-visible"
          @update:model-value="navigate"
        />
      </div>
      <!-- The local commands (Settings & Preferences): the items below each heading in two columns -->
      <SettingsDialogListGroup
        v-for="group in sections.localGroups"
        :key="[...group.keys()][0]"
        :list="group"
        :highlight="activeLabel"
        class="spotlight-two-columns h-auto overflow-visible"
        @update:model-value="navigate"
      />
      <SettingsDialogListGroup
        v-if="sections.after.size > 0"
        :list="sections.after"
        :highlight="activeLabel"
        class="h-auto overflow-visible"
        @update:model-value="navigate"
      />
    </div>
    <section
      v-else
      class="d-flex flex-column justify-content-center align-items-center p-3 w-100 text-secondary"
      style="height: 9999px"
    >
      <ErrorMessageAlert v-if="errorMessage" :error-message="errorMessage" />
      <ul v-else-if="loading" class="placeholder-glow placeholder-list">
        <li v-for="i in GHOST_ROWS" :key="i" :class="['w-100 mb-2', {'mt-3': i !== 1}]">
          <i class="placeholder placeholder-lg ratio ratio-1x1 me-2"></i>
          <span :class="['placeholder placeholder-lg', `col-${placeholderNum(7, 12)}`]"></span>
        </li>
      </ul>
      <template v-else-if="lastSearch === undefined">
        <Icon icon="material-symbols:search" class="fs-1 mb-2" />
        <p class="m-0">{{ translate('Find pages throughout Sphere') }}</p>
      </template>
      <template v-else-if="list.size === 0">
        <h2 class="mb-2">{{ lenny }}</h2>
        <p class="m-0">{{ translate('No results for') }} "{{ lastSearch }}"</p>
      </template>
    </section>
    <!-- Below every search, whatever it found -->
    <footer v-if="showFeedbackPrompt" class="spotlight-feedback flex-shrink-0 small text-secondary">
      {{
        translate(
          "Didn't find what you were looking for? Try again or let us know what you are looking for.",
        )
      }}
      <BootstrapButton
        name="spotlight.feedback"
        variant="link"
        size="sm"
        class="d-inline-flex p-0 align-baseline"
        @click="openFeedback"
      >
        {{ translate('feedback') }}
      </BootstrapButton>
    </footer>
  </DialogDrawer>
</template>
<script lang="ts" setup>
import DialogDrawer from '@/components/shared/custom/DialogDrawer/DialogDrawer.vue';
import {useDebounceFn, useIntervalFn} from '@vueuse/core';
import {
  apiClearRecentSearches,
  apiGetPromotedSearches,
  apiGetRecentPages,
  apiGetRecentSearches,
  searchCmsPages,
  type PromotedSearch,
  type SearchPage,
} from '@/api/apolloPlatform/cms';
import {translate as getTranslation} from '@/translate';
import {useSearchCommands, type SearchCommand} from '@/composables/useSearchCommands';
const dialog = useTemplateRef<InstanceType<typeof DialogDrawer>>('dialog');
const emit = defineEmits<{navigate: [meta: SpherePageMeta<SpherePageType>]}>();
const {placeholderNum} = usePlaceholderNum();
const {isMobile} = useBsBreakpoints();
const {translate} = useLocalisation();

// [label, icon, key]: pages can share a title, so they are told apart by their key
type Item = [string, string, string?];
const pageKey = (page: {id: number}) => `page:${page.id}`;

const model = ref('');
const lastSearch = ref(); // Handy for starting off
let results: SpherePage<SpherePageType>[] = [];
let commands: SearchCommand[] = [];
const {execute, icons, headings, localCommands, describe} = useSearchCommands();
// What is currently listed in the start state (as filtered by what is typed)
let recentSearches = new Set<string>(); // The recent searches that are listed
let startPages: SearchPage[] = []; // The visited & promoted pages that are listed
// All of the start state, that is filtered without any API call while typing
const GHOST_ROWS = 5;
const awaitingResults = ref(false); // A search is under way: the start state keeps its headings, not its items
const startStateActive = ref(false); // Not a search yet: what is listed is the start state
const MAX_RECENT_SEARCHES = 10;
let startRecent: string[] = [];
let startVisited: SearchPage[] = [];
let startPromoted: {page: SearchPage; keywords: string[]}[] = [];
const promotedSearches = ref<PromotedSearch[]>([]);
// Whether there are any recent searches, recent pages or suggestions at all: not what is left of them while typing
const hasStartItems = ref(false);
// Nothing to tell apart from the local commands: no headings, until something is typed (then they stick)
const withoutHeadings = computed(
  () => startStateActive.value && !hasStartItems.value && model.value.trim() === '',
);

// What the search can do, rotating in the search bar to draw attention to it
const HINT_INTERVAL = 8000;
const MAX_HINTS = 12;
const hints = computed(() => {
  const keywords = [...new Set(promotedSearches.value.map((p) => p.keyword))].map(
    (keyword) => `${translate('Try')} "${keyword}"`,
  );
  const actions = localCommands.value.map(describe);
  const mixed: string[] = []; // Alternate, so both are seen early on
  for (let i = 0; i < Math.max(keywords.length, actions.length); i++) {
    if (i < keywords.length) mixed.push(keywords[i]);
    if (i < actions.length) mixed.push(actions[i]);
  }
  return [translate('Search or go to...'), ...mixed.slice(0, MAX_HINTS)];
});
const hintIndex = ref(0);
const isOpen = ref(false); // While the dialog is open, the bar stays on its hint instead of rotating
const hint = computed(() => hints.value[hintIndex.value % hints.value.length]);
// Never paused or restarted: opening & closing the dialog doesn't touch the rotation's timing
useIntervalFn(() => {
  if (!isOpen.value) hintIndex.value++;
}, HINT_INTERVAL);

const list = reactive(new Map<string | undefined, Item[]>());

const loading = ref(false);
const errorMessage = ref<string>();
// Once a search was made & answered: not while waiting, nor when it failed
const showFeedbackPrompt = computed(
  () => lastSearch.value !== undefined && !loading.value && !errorMessage.value,
);

let requestId = 0; // Guards against stale (slower) responses overwriting newer ones

const addCommandsToList = (items: SearchCommand[]) => {
  for (const command of items) {
    const heading = translate(headings[command.action]);
    const entry: [string, string] = [command.label, command.icon ?? icons[command.action]];
    list.set(heading, [...(list.get(heading) ?? []), entry]);
  }
};

// Recent searches, recent pages & promoted searches are conveniences, the local commands are still
// useful without them. Fail quietly for the user, but not for whoever wonders why one is missing.
const warn = (what: string, reason: unknown) => {
  // eslint-disable-next-line no-console
  console.warn(`Spotlight: could not load the ${what}`, reason);
};

const loadPromotedSearches = async () => {
  try {
    promotedSearches.value = (await apiGetPromotedSearches()).data;
  } catch (error) {
    warn('promoted searches', error); // Keep what we had
  }
};
loadPromotedSearches(); // Also feeds the hints rotating in the search bar

/** Whether every word typed is part of (at least) one of the texts. */
const matcher = (typed: string) => {
  const tokens = typed.toLowerCase().split(/\s+/).filter(Boolean);
  return (...texts: string[]) =>
    tokens.every((token) => texts.some((text) => text.toLowerCase().includes(token)));
};

/** The local commands that match what is typed. Also in the other language, to find them either way. */
const matchingLocalCommands = (typed: string) => {
  const matches = matcher(typed);
  return localCommands.value.filter((c) =>
    matches(c.label, translate(c.label), getTranslation('nl', c.label), describe(c)),
  );
};

/** Show the start state's items that match everything typed, locally. */
const renderStartState = () => {
  const matches = matcher(model.value);
  hasStartItems.value = startRecent.length + startVisited.length + startPromoted.length > 0;

  commands = matchingLocalCommands(model.value);
  const waiting = awaitingResults.value;
  if (waiting) commands = [];
  const recentQueries = waiting
    ? []
    : startRecent.filter((query) => matches(query)).slice(0, MAX_RECENT_SEARCHES);
  const visited = waiting ? [] : startVisited.filter((page) => matches(page.title));
  // Promoted pages are found by their keywords as well as by their title
  const pages = waiting
    ? []
    : startPromoted
        .filter(({page, keywords}) => matches(page.title, ...keywords))
        .map(({page}) => page);
  recentSearches = new Set(recentQueries);
  startPages = [...visited, ...pages];

  // A heading stays as long as there is something under it at all: typing only narrows the items down
  list.clear();
  if (recentQueries.length > 0 || startRecent.length > 0) {
    list.set(
      translate('Recent searches'),
      recentQueries.map((query) => [query, 'history']),
    );
  }
  if (visited.length > 0 || startVisited.length > 0) {
    list.set(
      translate('Recently visited'),
      visited.map((page) => [page.title, page.icon || 'article', pageKey(page)]),
    );
  }
  if (pages.length > 0 || startPromoted.length > 0) {
    list.set(
      translate('Suggested'),
      pages.map((page) => [page.title, page.icon || 'article', pageKey(page)]),
    );
  }
  for (const {action} of localCommands.value) {
    const heading = translate(headings[action]);
    if (!list.has(heading)) list.set(heading, []);
  }
  addCommandsToList(commands);
};

const buildStartState = (recent: string[], visited: SearchPage[]) => {
  // Commands & recent searches are selected by label: show each label once, local commands first
  const commandLabels = new Set(localCommands.value.map((c) => c.label.toLowerCase()));
  const seen = new Set(commandLabels);
  const unseen = (label: string) =>
    !seen.has(label.toLowerCase()) && !!seen.add(label.toLowerCase());
  // Pages are selected by their id: pages may share a title, but are listed once
  const seenPages = new Set<number>();
  const unseenPage = (page: SearchPage) =>
    !commandLabels.has(page.title.toLowerCase()) &&
    !seenPages.has(page.id) &&
    !!seenPages.add(page.id);

  startRecent = recent.filter(unseen);
  startVisited = visited.filter(unseenPage);
  // A page promoted for several keywords is listed once
  const byPage = new Map<number, {page: SearchPage; keywords: string[]}>();
  for (const {keyword, page} of promotedSearches.value) {
    const entry = byPage.get(page.id) ?? {page, keywords: []};
    entry.keywords.push(keyword);
    byPage.set(page.id, entry);
  }
  startPromoted = [...byPage.values()].filter(({page}) => unseenPage(page));
  startStateActive.value = true;
  renderStartState();
};

// Typing narrows the start state down, until a search replaces it with results
watch(model, () => {
  if (startStateActive.value) renderStartState();
});

/**
 * What is shown before a search was made: recent searches, the recently visited & promoted pages, then the local commands.
 */
const showStartState = async () => {
  const currentRequest = ++requestId;
  awaitingResults.value = false;
  lastSearch.value = undefined;
  errorMessage.value = undefined;
  loading.value = false;
  results = [];
  buildStartState([], []); // Instantly, without the API

  const [recent, visited] = await Promise.allSettled([
    (async () => apiGetRecentSearches(MAX_RECENT_SEARCHES))(),
    (async () => apiGetRecentPages())(),
    loadPromotedSearches(),
  ]);
  if (currentRequest !== requestId) return;
  if (recent.status === 'rejected') warn('recent searches', recent.reason);
  if (visited.status === 'rejected') warn('recent pages', visited.reason);
  buildStartState(
    recent.status === 'fulfilled' ? recent.value.data.map((r) => r.query) : [],
    visited.status === 'fulfilled' ? visited.value.data.map((v) => v.page) : [],
  );
};

/** Forget the recent searches: they disappear from the start state right away. */
const clearRecent = async () => {
  const previous = startRecent;
  startRecent = [];
  renderStartState();
  try {
    await apiClearRecentSearches();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Spotlight: could not clear the recent searches', error);
    startRecent = previous; // Still remembered: list them again
    if (startStateActive.value) renderStartState();
  }
};

const open = () => {
  isOpen.value = true;
  dialog.value?.openModal();
  showStartState();
};

const search = async () => {
  const query = model.value.trim();
  const currentRequest = ++requestId;
  highlightAfterSearch = activeLabel.value;
  if (query === '') {
    highlightAfterSearch = undefined;
    await showStartState();
    return;
  }
  // Keep the start state's headings, without their items, while waiting for the results
  awaitingResults.value = true;
  startStateActive.value = true;
  renderStartState();
  loading.value = true;
  errorMessage.value = undefined;
  try {
    lastSearch.value = model.value;
    const response = await searchCmsPages(model.value);
    if (currentRequest !== requestId) return;
    awaitingResults.value = false;
    startStateActive.value = false;
    recentSearches = new Set();
    startPages = [];
    const excluded = ['cms.CoreIndexPage', 'cms.CategoryIndexPage'];
    const promoted = response.data.promoted ?? [];
    const promotedIds = new Set(promoted.map((p) => p.meta.id));
    results = [
      ...promoted,
      ...response.data.results.filter(
        (r) => excluded.includes(r.meta.type) === false && !promotedIds.has(r.meta.id),
      ),
    ];
    const pages: Item[] = [];
    const reports: Item[] = [];
    const documents: Item[] = [];
    const folders: Item[] = [];
    const suggested: Item[] = [];
    // Keep the local commands that match, as they were listed while typing: the search doesn't know them.
    // Results are selected by title, so a page titled like a command takes its place.
    const titles = new Set(results.map((r) => r.meta.title.toLowerCase()));
    commands = [
      ...(response.data.commands ?? []),
      ...matchingLocalCommands(query).filter((c) => !titles.has(c.label.toLowerCase())),
    ];

    for (const {meta} of results) {
      const icon = meta.icon || 'article';
      const item: Item = [meta.title, icon, pageKey(meta)];
      if (promotedIds.has(meta.id)) {
        suggested.push(item);
        continue;
      }
      switch (meta.type) {
        case 'cms.ReportPage':
          reports.push(item);
          break;
        case 'cms.DocumentPage':
          documents.push(item);
          break;
        case 'cms.FolderPage':
          folders.push(item);
          break;
        default:
          pages.push(item);
          break;
      }
    }

    list.clear();
    addCommandsToList(commands);
    if (suggested.length > 0) list.set(translate('Suggested'), suggested);
    if (pages.length > 0) list.set(translate('Pages'), pages);
    if (reports.length > 0) list.set(translate('Reports'), reports);
    if (documents.length > 0) list.set(translate('Documents'), documents);
    if (folders.length > 0) list.set(translate('Folders'), folders);
  } catch (error: any) {
    if (currentRequest !== requestId) return;
    awaitingResults.value = false;
    startStateActive.value = false;
    list.clear();
    errorMessage.value = error;
  } finally {
    if (currentRequest === requestId) {
      loading.value = false;
      if (labels.value.length === 0) highlightAfterSearch = undefined; // Nothing to highlight
    }
  }
};

// Enter searches right away. While typing narrows down what is listed locally (the start state: the
// local commands, pages & recent searches), the search only follows after a long pause, to leave time to
// pick one of those. Without any of those left, it follows the usual short pause.
// Not once the dialog was closed in the meantime: that would list things in a dialog nobody sees, nor
// when Enter already searched this.
const LOCAL_SEARCH_DELAY = 3000;
const SEARCH_DELAY = 500;
const SEARCH_MAX_WAIT = 10000;
const LOCAL_SEARCH_MAX_WAIT = 60000; // Plenty of time to pick a local result
const hasLocalMatches = computed(
  () =>
    startStateActive.value &&
    !awaitingResults.value &&
    [...list.values()].some((items) => items.length > 0),
);
const debouncedFn = useDebounceFn(
  () => {
    if (dialog.value?.open && lastSearch.value !== model.value) search();
  },
  () => (hasLocalMatches.value ? LOCAL_SEARCH_DELAY : SEARCH_DELAY),
  {maxWait: () => (hasLocalMatches.value ? LOCAL_SEARCH_MAX_WAIT : SEARCH_MAX_WAIT)},
);
// What is listed follows from what was typed: wait for that to be rendered, as the delay depends on it
const onInput = async () => {
  await nextTick();
  debouncedFn();
};

// Move through what is listed with the arrow keys, without leaving the input

/**
 * The list as laid out: what comes before the local commands, the local commands (a list of their own
 * each, with the items in two columns), and what comes after. The arrow keys follow this order, so
 * that includes down the left column & then down the right one: that is how the columns are filled.
 */
const sections = computed(() => {
  const localHeadings = new Set(localCommands.value.map((c) => translate(headings[c.action])));
  const leadingHeading = startStateActive.value ? translate('Recent searches') : undefined;
  // Side by side, left to right: the pages & the suggestions
  const rowHeadings = startStateActive.value
    ? new Set(['Recently visited', 'Suggested'].map((h) => translate(h)))
    : new Set<string | undefined>();
  const before = new Map<string | undefined, Item[]>();
  const after = new Map<string | undefined, Item[]>();
  const leading: Map<string | undefined, Item[]>[] = [];
  const row: Map<string | undefined, Item[]>[] = [];
  const localGroups: Map<string | undefined, Item[]>[] = [];
  for (const [heading, items] of list) {
    if (localHeadings.has(heading as string)) localGroups.push(new Map([[heading, items]]));
    else if (heading === leadingHeading) leading.push(new Map([[heading, items]]));
    else if (rowHeadings.has(heading)) row.push(new Map([[heading, items]]));
    else
      (localGroups.length + row.length + leading.length === 0 ? before : after).set(heading, items);
  }
  return {before, leading, row, localGroups, after};
});

/** Whether the layout is a single column: see the breakpoint in the styles. */
const stacked = useBsBreakpoints().breakpoints.smaller('md');

/**
 * The items as laid out in blocks of columns, which the arrow keys follow: up & down go through
 * the columns in turn (left to right) & left & right go to the item next to it.
 */
const layout = computed<string[][][]>(() => {
  const names = (group: Map<string | undefined, Item[]>) =>
    [...group.values()].flatMap((items) => items.map(([name, , key]) => key ?? name));
  // The items below a heading fill the left column first, in as many rows as needed
  const split = (items: string[]) =>
    stacked.value
      ? [items]
      : [items.slice(0, Math.ceil(items.length / 2)), items.slice(Math.ceil(items.length / 2))];
  const {before, leading, row, localGroups, after} = sections.value;
  return [
    [names(before)],
    ...leading.map((group) => split(names(group))),
    stacked.value ? [] : row.map(names), // Side by side, or else one below the other
    ...(stacked.value ? row.map((group) => [names(group)]) : []),
    ...localGroups.map((group) => split(names(group))),
    [names(after)],
  ]
    .map((columns) => columns.filter((column) => column.length > 0))
    .filter((columns) => columns.length > 0);
});

const labels = computed(() => layout.value.flat(2));
const activeIndex = ref(-1);
const activeLabel = computed(() => labels.value[activeIndex.value]);
// What was highlighted when a search started: once its results are in, that is highlighted again
// (when it is part of them), or else the first result
let highlightAfterSearch: string | undefined;
watch(labels, (now, before) => {
  if (highlightAfterSearch !== undefined && !loading.value && now.length > 0) {
    activeIndex.value = Math.max(now.indexOf(highlightAfterSearch), 0);
    highlightAfterSearch = undefined;
    return;
  }
  // Keep the selection on the same item if it is still listed
  const previous = before[activeIndex.value];
  activeIndex.value = previous === undefined ? -1 : now.indexOf(previous);
});
const move = (step: 1 | -1) => {
  const count = labels.value.length;
  if (count === 0) return;
  // Cycles through the items & "nothing", so Enter can still search what was typed
  const next = activeIndex.value + step;
  activeIndex.value = next >= count ? -1 : next < -1 ? count - 1 : next;
};

/** Left & right go to the item next to the active one, in the same row as far as there is one. */
const sideways = (step: 1 | -1, event: KeyboardEvent) => {
  const label = activeLabel.value;
  if (label === undefined) return; // Nothing is active: this moves the cursor in the input
  for (const columns of layout.value) {
    const column = columns.findIndex((names) => names.includes(label));
    if (column === -1) continue;
    const next = columns[column + step];
    if (next === undefined) return; // This is the last column: that is for the cursor
    const row = Math.min(columns[column].indexOf(label), next.length - 1);
    activeIndex.value = labels.value.indexOf(next[row]);
    event.preventDefault();
    return;
  }
};

const openFeedback = async () => {
  await execute({
    action: 'open_settings',
    label: 'Feedback',
    payload: {tab: 'Feedback'},
  });
  dialog.value?.close(); // N.B. closing resets the dialog (@close)
  model.value = '';
};

const navigate = async (key: string) => {
  const command = commands.find((c) => c.label === key);
  if (command) {
    loading.value = true;
    try {
      await execute(command);
      dialog.value?.close(); // N.B. closing resets the dialog (@close)
      model.value = '';
    } catch (error: any) {
      errorMessage.value = String(error);
      list.clear();
    } finally {
      loading.value = false;
    }
    return;
  }
  if (recentSearches.has(key)) {
    model.value = key;
    await search();
    return;
  }
  // N.B. This is a page & not a navigation node.
  const page =
    startPages.find((p) => pageKey(p) === key) ??
    results.find((r) => pageKey(r.meta) === key)?.meta;
  if (page) {
    emit('navigate', page as SpherePageMeta<SpherePageType>);
    dialog.value?.close();
    model.value = '';
  }
};

const enter = async () => {
  if (activeLabel.value !== undefined) await navigate(activeLabel.value);
  else await search();
};

const getLenny = () => {
  const lenny = ["(='X'=)", '(>_<)', '(o^^o)', '(^_^)', '/(o_o)/', '(˚Δ˚)', '¯\\_(ツ)_/¯'];
  const random = Math.floor(Math.random() * lenny.length);
  return lenny[random];
};
const lenny = ref(getLenny());

const reset = () => {
  requestId++; // Invalidate any in-flight request
  highlightAfterSearch = undefined;
  loading.value = false;
  errorMessage.value = undefined;
  model.value = '';
  lastSearch.value = undefined;
  list.clear();
  commands = [];
  startStateActive.value = false;
  awaitingResults.value = false;
  startRecent = [];
  startVisited = [];
  startPromoted = [];
  lenny.value = getLenny();
  isOpen.value = false;
};
const isMac = /mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent);
const shortcutLabel = isMac ? '⌘k' : 'Ctrl k';
window.addEventListener('keydown', (e: KeyboardEvent) => {
  const activeElement = document.activeElement as HTMLElement;
  const isEditable = activeElement?.matches('input, textarea') || activeElement?.isContentEditable;
  //should cover the native dialogs & bootstrap hopefully but I think those are mostly phased out
  const hasOpenDialog =
    document.querySelector('dialog[open]') || document.querySelector('[aria-modal="true"]');

  const isClosed = dialog.value?.open === false;
  const isModifierShortcut =
    e.key.toLowerCase() === 'k' && (isMac ? e.metaKey : e.ctrlKey) && !e.altKey && !e.shiftKey;

  if (isClosed && !hasOpenDialog && (isModifierShortcut || (e.key === '/' && !isEditable))) {
    e.preventDefault();
    open();
  } else if (e.key === 'Escape' && dialog.value?.open === true) {
    e.preventDefault();
    dialog.value.close();
    reset();
  }
});
</script>
<style lang="scss" scoped>
@use '@/scss/breakpoints/breakpoints' as *;

.dialog {
  margin-top: 6rem;
  max-height: 70%;
  max-width: 48rem;
  border-radius: 0.75rem;

  :deep(.card-body) {
    display: flex;
    flex-direction: column;
    padding: 0;
  }

  @include media-breakpoint-down(md, $grid-breakpoints) {
    $margin: calc(var(--shell-header-height) * 1.5);

    margin-top: $margin;
    max-height: calc(100vh - $margin);
    width: 100vw;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

// Long titles are cut short, rather than flowing into the next column
@mixin truncate-items {
  :deep(.btn) {
    min-width: 0;
    overflow: hidden;

    span {
      flex-shrink: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

// Next to the "Recent searches" heading
.spotlight-clear {
  position: absolute;
  top: 0;
  right: 0;
  font-size: 0.75rem;
}

.spotlight-key {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  color: inherit;
  background: transparent;
  border: var(--bs-border-width) solid currentcolor;
  border-radius: 0.375rem;
  font-family: inherit;
  font-size: 0.75rem;
}

.spotlight-feedback {
  padding: 0.5rem 1rem;
  border-top: var(--bs-border-width) solid var(--bs-border-color);
}

.dialog.spotlight-dialog {
  // The centred dialog is only 28rem wide by default (see DialogDrawer)
  @media (width >= 768px) {
    max-width: min(60rem, calc(100vw - 4rem));
    max-height: 80vh;
  }
}

.spotlight-sections {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  &.spotlight-flat :deep(h6) {
    display: none;
  }
}

.spotlight-row {
  display: grid;
  grid-template-columns: repeat(var(--columns, 1), minmax(0, 1fr));
  column-gap: 1rem;

  .spotlight-column {
    min-width: 0; // The list is 12rem wide at least, wider than a column can be

    @include truncate-items;
  }

  @include media-breakpoint-down(md, $grid-breakpoints) {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 0.75rem;
  }
}

.spotlight-sections .spotlight-two-columns {
  // The heading spans both columns, the items below it fill the left column first & then the right
  display: block;
  column-count: 2;
  column-gap: 1rem;

  :deep(h6) {
    column-span: all;
  }

  @include truncate-items;

  :deep(.btn) {
    break-inside: avoid;
  }

  @include media-breakpoint-down(md, $grid-breakpoints) {
    column-count: 1;
  }
}

.spotlight-dialog-loading {
  :deep(form) {
    overflow-y: hidden !important;
  }
}

.placeholder-list {
  width: 100%;
  height: 100%;
  padding: 0%;
  margin: 0;
  overflow-y: hidden;
  list-style: none;

  i {
    width: 1.2rem;
  }
}

// Above the headings, as tall as its rows
.placeholder-list.spotlight-ghost {
  height: auto;
  flex-shrink: 0;
  padding: 0.5rem 0.5rem 0;
}

:deep(.btn-close) {
  display: none;
}

.hint-enter-active,
.hint-leave-active {
  transition:
    opacity 0.3s,
    transform 0.3s;
}

.hint-enter-from {
  opacity: 0;
  transform: translateY(0.5rem);
}

.hint-leave-to {
  opacity: 0;
  transform: translateY(-0.5rem);
}

@media (prefers-reduced-motion: reduce) {
  .hint-enter-active,
  .hint-leave-active {
    transition: none;
  }
}
</style>
